import {
  ContainerFormDefaultValues,
  ContainerFormProps,
  ContainerFormRawValues,
  ContainerFormType
} from "./ContainerForm";
import {useTranslation} from "react-i18next";
import {useForm} from "@mantine/form";
import {ContainerEntity, getObjectId, isStringUndefinedOrBlank, scadaMode} from "@ots-frontend/ots-data";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";

export const toFormDefaultsFromContainer = (container: ContainerEntity | undefined): ContainerFormDefaultValues => {
  return {
    name: container?.name,
    abbr: container?.abbr,
    barcode: container?.barcode,
    leaseId: container?.leaseId,
    leaseName: container?.leaseName,
    afe: container?.afe,
    afePrefix: container?.afePrefix,
    afeSuffix: container?.afeSuffix,
    type: container?.type,
    size: {
      number: container?.size,
      units: container?.sizeUom,
    },
    weight: {
      number: container?.weight,
      units: container?.weightUom,
    },
    dimensions: {
      height: container?.height,
      diameter: container?.diameter,
      units: container?.diameterUom,
    },
    company: container?.owningCompanyId,
    freightType: container?.waterTypeId,
    site: container?.siteId,
    cluster: container?.containerClusterId,
    scadaMode: container?.scadaMode,
    scadaModeReason: container?.scadaModeReason,
    dispatchIgnoreMaxNonPrimaryVolume: container?.dispatchIgnoreMaxNonPrimaryVolume,
    doNotDispatch: container?.doNotDispatch,
    doNotUse: container?.doNotUse,
    active: container?.active,
  };
};

const getInitialValues = (defaultValues: ContainerFormDefaultValues | undefined): ContainerFormRawValues => {
  return {
    name: defaultValues?.name,
    abbr: defaultValues?.abbr,
    barcode: defaultValues?.barcode,
    leaseId: defaultValues?.leaseId,
    leaseName: defaultValues?.leaseName,
    afe: defaultValues?.afe,
    afePrefix: defaultValues?.afePrefix,
    afeSuffix: defaultValues?.afeSuffix,
    type: defaultValues?.type?.code,
    company: getObjectId(defaultValues?.company),
    freightType: getObjectId(defaultValues?.freightType),
    site: getObjectId(defaultValues?.site),
    cluster: getObjectId(defaultValues?.cluster),
    size: defaultValues?.size?.number,
    sizeUom: defaultValues?.size?.units,
    weight: defaultValues?.weight?.number,
    weightUom: defaultValues?.weight?.units,
    dimenHeight: defaultValues?.dimensions?.height,
    dimenDiameter: defaultValues?.dimensions?.diameter,
    dimenUom: defaultValues?.dimensions?.units,
    scadaMode: defaultValues?.scadaMode?.code,
    scadaModeReason: defaultValues?.scadaModeReason,
    dispatchIgnoreMaxNonPrimaryVolume: defaultValues?.dispatchIgnoreMaxNonPrimaryVolume,
    doNotDispatch: defaultValues?.doNotDispatch,
    doNotUse: defaultValues?.doNotUse,
    active: defaultValues?.active,
  }
}

export type UseContainerFormProps = Pick<ContainerFormProps, 'defaultValues'> & {}

export function useContainerForm(props: UseContainerFormProps): ContainerFormType {
  const {
    defaultValues
  } = props

  const {t} = useTranslation(["common"])

  const {uoms} = useApp()

  const form = useForm({
    initialValues: getInitialValues({
      ...defaultValues,
      size: {
        number: defaultValues?.size?.number,
        units: defaultValues?.size?.units ?? uoms?.volume,
      },
      weight: {
        number: defaultValues?.weight?.number,
        units: defaultValues?.weight?.units ?? uoms?.weight,
      },
      dimensions: {
        height: defaultValues?.dimensions?.height,
        diameter: defaultValues?.dimensions?.diameter,
        units: defaultValues?.dimensions?.units ?? uoms?.linear,
      },
      scadaMode: defaultValues?.scadaMode ?? scadaMode.unknown,
      active: typeof defaultValues?.active !== 'undefined' ? defaultValues.active : true,
    }),
    validate: {
      name: (value) => value ? undefined : t("common.validation.required", "Required"),
      size: (value) => value ? undefined : t("common.validation.required", "Required"),
      sizeUom: (value) => value ? undefined : t("common.validation.required", "Required"),
      company: (value) => value ? undefined : t("common.validation.required", "Required"),
      freightType: (value) => value ? undefined : t("common.validation.required", "Required"),
      site: (value) => value ? undefined : t("common.validation.required", "Required"),
      weightUom: (value, values) => isStringUndefinedOrBlank(value) && (values.weight) ? t("common.validation.required", "Required") : undefined,
      dimenUom: (value, values) => isStringUndefinedOrBlank(value) && ((values.dimenHeight) || (values.dimenDiameter)) ? t("common.validation.required", "Required") : undefined,
    },
  })

  return form
}
